/**
 * KHIYARSA (Phase 2): minimal EN/AR strings for the cloud UI. Kept local to
 * the khiyarsa layer so we don't touch package locale files.
 */
import { useAppLangCode } from "../../app-language/language-state";

const en = {
  signIn: "Sign in",
  signOut: "Sign out",
  createAccount: "Create account",
  email: "Email",
  password: "Password",
  displayName: "Name (optional)",
  myProjects: "My projects",
  saveToCloud: "Save to my projects...",
  projectName: "Project name",
  save: "Save",
  cancel: "Cancel",
  open: "Open",
  rename: "Rename",
  history: "History",
  delete: "Delete",
  restore: "Restore",
  deleteForever: "Delete forever",
  trash: "Recently deleted",
  backToProjects: "Back to projects",
  newBlankProject: "New blank project",
  saveCanvasAsProject: "Save current canvas as a project",
  noProjects: "No projects yet.",
  emptyTrash: "Nothing in trash.",
  untitled: "Untitled",
  statusSaved: "Saved",
  statusSaving: "Saving…",
  statusOffline: "Offline — will retry",
  statusConflict: "Sync conflict",
  conflictTitle: "This project changed somewhere else",
  conflictBody:
    "The cloud copy of this project was updated from another device or tab, and this canvas has changes of its own. Choose which version to keep — either choice keeps a snapshot in the project history.",
  conflictKeepMine: "Keep this canvas",
  conflictUseCloud: "Use cloud version",
  conflictSaveCopy: "Save canvas as a copy, then load cloud version",
  overwriteWarningTitle: "Replace current canvas?",
  overwriteWarningBody:
    "Opening a project will replace what is currently on this canvas. The current canvas is not saved to any project.",
  overwriteConfirm: "Open anyway",
  restoreRevisionConfirm:
    "Restore this version? The current version will be kept as a snapshot in history.",
  deleteConfirm: "Move this project to trash? It will be kept for 30 days.",
  purgeConfirm: "Permanently delete this project? This cannot be undone.",
  closeProject: "Stop syncing (keep canvas)",
  loginFailed: "Wrong email or password.",
  emailExists: "An account with this email already exists.",
  tooManyAttempts: "Too many attempts — try again in a few minutes.",
  genericError: "Something went wrong. Please try again.",
  passwordTooShort: "Password must be at least 8 characters.",
  revisions: "Version history",
  noRevisions: "No older versions yet. Snapshots are taken as you work.",
  current: "current",
  signedInAs: "Signed in as",
  projectsHint: "Private projects are synced to your khiyarsa account.",
  bannerConflict: "Sync conflict — this project changed elsewhere. Tap to resolve.",
  bannerOffline: "Offline — changes are saved on this device and will sync when back online.",
  searchProjects: "Search projects…",
  noSearchResults: "No projects match your search.",
  duplicate: "Duplicate",
  storageUsed: "storage used",
};

const ar: typeof en = {
  signIn: "تسجيل الدخول",
  signOut: "تسجيل الخروج",
  createAccount: "إنشاء حساب",
  email: "البريد الإلكتروني",
  password: "كلمة المرور",
  displayName: "الاسم (اختياري)",
  myProjects: "مشاريعي",
  saveToCloud: "حفظ في مشاريعي...",
  projectName: "اسم المشروع",
  save: "حفظ",
  cancel: "إلغاء",
  open: "فتح",
  rename: "إعادة تسمية",
  history: "السجلّ",
  delete: "حذف",
  restore: "استعادة",
  deleteForever: "حذف نهائي",
  trash: "المحذوفة مؤخرًا",
  backToProjects: "عودة إلى المشاريع",
  newBlankProject: "مشروع جديد فارغ",
  saveCanvasAsProject: "حفظ اللوحة الحالية كمشروع",
  noProjects: "لا توجد مشاريع بعد.",
  emptyTrash: "سلة المحذوفات فارغة.",
  untitled: "بدون عنوان",
  statusSaved: "تم الحفظ",
  statusSaving: "جارٍ الحفظ…",
  statusOffline: "غير متصل — ستتم إعادة المحاولة",
  statusConflict: "تعارض في المزامنة",
  conflictTitle: "تغيّر هذا المشروع في مكان آخر",
  conflictBody:
    "تم تحديث النسخة السحابية من هذا المشروع من جهاز أو تبويب آخر، وهذه اللوحة تحتوي تغييرات خاصة بها. اختر النسخة التي تريد الإبقاء عليها — في كلتا الحالتين يُحفظ الإصدار الآخر في سجلّ المشروع.",
  conflictKeepMine: "الإبقاء على هذه اللوحة",
  conflictUseCloud: "استخدام النسخة السحابية",
  conflictSaveCopy: "حفظ اللوحة كنسخة ثم تحميل النسخة السحابية",
  overwriteWarningTitle: "استبدال اللوحة الحالية؟",
  overwriteWarningBody:
    "فتح مشروع سيستبدل ما هو موجود حاليًا على اللوحة. اللوحة الحالية غير محفوظة في أي مشروع.",
  overwriteConfirm: "فتح على أي حال",
  restoreRevisionConfirm:
    "استعادة هذا الإصدار؟ سيُحفظ الإصدار الحالي كنسخة في السجلّ.",
  deleteConfirm: "نقل هذا المشروع إلى سلة المحذوفات؟ سيُحتفظ به لمدة 30 يومًا.",
  purgeConfirm: "حذف هذا المشروع نهائيًا؟ لا يمكن التراجع عن ذلك.",
  closeProject: "إيقاف المزامنة (مع بقاء اللوحة)",
  loginFailed: "البريد الإلكتروني أو كلمة المرور غير صحيحة.",
  emailExists: "يوجد حساب بهذا البريد الإلكتروني بالفعل.",
  tooManyAttempts: "محاولات كثيرة — حاول مجددًا بعد دقائق.",
  genericError: "حدث خطأ ما. حاول مرة أخرى.",
  passwordTooShort: "يجب أن تكون كلمة المرور 8 أحرف على الأقل.",
  revisions: "سجلّ الإصدارات",
  noRevisions: "لا توجد إصدارات أقدم بعد. تُلتقط لقطات تلقائيًا أثناء عملك.",
  current: "الحالي",
  signedInAs: "مسجّل الدخول باسم",
  projectsHint: "المشاريع الخاصة تُزامَن مع حسابك في خيارصة.",
  bannerConflict: "تعارض في المزامنة — تغيّر هذا المشروع في مكان آخر. اضغط للحل.",
  bannerOffline: "غير متصل — التغييرات محفوظة على هذا الجهاز وستُزامَن عند عودة الاتصال.",
  searchProjects: "البحث في المشاريع…",
  noSearchResults: "لا توجد مشاريع مطابقة لبحثك.",
  duplicate: "نسخ",
  storageUsed: "من التخزين المستخدم",
};

export type CloudStringKey = keyof typeof en;

export const useCloudT = () => {
  const [langCode] = useAppLangCode();
  const dict = langCode?.startsWith("ar") ? ar : en;
  return (key: CloudStringKey) => dict[key] ?? en[key];
};

export const cloudT = (langCode: string, key: CloudStringKey) =>
  (langCode?.startsWith("ar") ? ar : en)[key] ?? en[key];
